/**
 * @fileoverview Client Profile page component
 *
 * Displays client profile information with inline editing capabilities:
 * - Read-only fields: Client name, email, phone
 * - Editable fields: Mailing address, secondary email, secondary phone
 * - "Update Information" button toggles edit mode
 * - Save/Cancel buttons for edit mode
 * - Alerts for edit restrictions and secondary email access info
 *
 * @requires react
 * @requires react-icons/fa
 * @requires ../hooks/useClientProfile
 * @requires ../hooks/useClientProject
 * @requires ../firebase
 * @version 1.0.0
 * @created 2025-12-04
 */

// src/components/ClientProfile.js
// Client Profile page with inline editing functionality

import React, { useState, useEffect } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "../../firebase";
import { useClientProfile } from "../../hooks/useClientProfile";
import { useClientProject } from "../../hooks";
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaUserEdit,
  FaSave,
  FaTimes,
  FaInfoCircle,
  FaExclamationTriangle,
  FaCheckCircle,
  FaSignInAlt,
} from "react-icons/fa";
import "./ClientProfile.css";

/**
 * ClientProfile component
 * Displays and manages client profile information
 * @returns {JSX.Element} Client profile page
 */

function ClientProfile() {
  const [user] = useAuthState(auth);
  const { error: projectError } = useClientProject();
  const { profile, isLoading, error, updateProfileMutation, isUpdating } =
    useClientProfile(user?.email);

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);

  // Form state for editable fields
  const [formData, setFormData] = useState({
    mailingAddress: "",
    secondaryEmail: "",
    secondaryPhone: "",
  });

  // Success message state
  const [showSuccess, setShowSuccess] = useState(false);

  // Initialize form data when profile loads
  useEffect(() => {
    if (profile) {
      setFormData({
        mailingAddress: profile.mailingAddress || "",
        secondaryEmail: profile.secondaryEmail || "",
        secondaryPhone: profile.secondaryPhone || "",
      });
    }
  }, [profile]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Enter edit mode
  const handleEditClick = () => {
    setIsEditing(true);
    setShowSuccess(false);
  };

  // Cancel editing - revert to original values
  const handleCancelClick = () => {
    setFormData({
      mailingAddress: profile?.mailingAddress || "",
      secondaryEmail: profile?.secondaryEmail || "",
      secondaryPhone: profile?.secondaryPhone || "",
    });
    setIsEditing(false);
  };

  // Save changes
  const handleSaveClick = async () => {
    try {
      await updateProfileMutation.mutateAsync({
        clientId: profile.id,
        updates: {
          mailingAddress: formData.mailingAddress,
          secondaryEmail: formData.secondaryEmail,
          secondaryPhone: formData.secondaryPhone,
        },
      });
      setIsEditing(false);
      setShowSuccess(true);
      // Hide success message after 5 seconds
      setTimeout(() => setShowSuccess(false), 5000);
    } catch (err) {
      console.error("Error saving profile:", err);
    }
  };

  // Format phone number for display
  const formatPhoneDisplay = (phone) => {
    if (!phone) return "Not provided";
    // Remove all non-numeric characters
    const cleaned = phone.replace(/\D/g, "");
    // Format as (XXX) XXX-XXXX if 10 digits
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(
        6,
      )}`;
    }
    return phone;
  };

  // Loading state
  if (isLoading) {
    return (
      <>
        <div className="component-loading">
          <div className="component-spinner"></div>
          <p>Loading your profile information...</p>
        </div>
      </>
    );
  }

  // Error state
  if (error) {
    return (
      <>
        <div className="dashboard-error">
          <FaExclamationTriangle />
          <h2>Unable to Load Client Profile</h2>
          <p>{projectError.message}</p>
          <button onClick={() => window.location.reload()}>Try Again</button>
        </div>
      </>
    );
  }

  // No profile found
  if (!profile) {
    return (
      <div className="client-profile">
        <div className="profile-container page-container">
          <div className="state-container">
            <FaUser className="state-icon" />
            <h2 className="state-title">Profile Not Found</h2>
            <p className="state-message">
              We couldn't find a profile associated with your account. Please
              contact your project coordinator for assistance.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="page-container">
        {/* Page Header */}
        <header className="page-header">
          <h1 className="page-title">Your Profile</h1>
          <p className="page-subtitle">View and Edit Your Information</p>
        </header>

        {/* Success Message */}
        {showSuccess && (
          <div className="profile-success-alert">
            <FaCheckCircle className="profile-alert-icon" />
            <span>Your profile has been updated successfully!</span>
          </div>
        )}

        {/* Update Error Alert */}
        {updateProfileMutation.isError && (
          <div className="profile-error-alert">
            <FaExclamationTriangle className="profile-alert-icon" />
            <span>
              Failed to update profile. Please try again or contact support.
            </span>
          </div>
        )}

        {/* Profile Card */}
        <div className="profile-card card">
          {/* Card Header with Edit Button */}
          <div className="profile-card-header">
            <div className="profile-header-info">
              <div className="profile-avatar">
                <FaUser />
              </div>
              <div className="profile-header-text">
                <h2 className="profile-name">{profile.clientName}</h2>
                <p className="profile-email" data-tooltip-br="logged in as">
                  <FaSignInAlt className="profile-email-icon" />
                  {user?.email}
                </p>
              </div>
            </div>
          </div>

          {/* Profile Fields */}
          <div className="profile-fields">
            {/* Contact Information Section */}
            <div className="profile-section">
              <h3 className="profile-section-title">
                Main Contact Information
              </h3>
              <div className="profile-field-items">
                {/* Primary Email - Read Only */}
                <div className="profile-field profile-field-readonly">
                  <div className="profile-field-icon">
                    <FaEnvelope />
                  </div>
                  <div className="profile-field-content">
                    <label className="profile-field-label">Primary Email</label>
                    <p className="profile-field-value">
                      {profile.email || "Not provided"}
                    </p>
                  </div>
                </div>

                {/* Primary Phone - Read Only */}
                <div className="profile-field profile-field-readonly">
                  <div className="profile-field-icon">
                    <FaPhone />
                  </div>
                  <div className="profile-field-content">
                    <label className="profile-field-label">Primary Phone</label>
                    <p className="profile-field-value">
                      {formatPhoneDisplay(profile.phone)}
                    </p>
                  </div>
                </div>
              </div>
              {/* Edit Mode Alert */}
              {isEditing && (
                <div className="profile-edit-alert">
                  <FaInfoCircle className="profile-alert-icon" />
                  <span>
                    To update primary email & phone, please contact your project
                    coordinator directly.
                  </span>
                </div>
              )}
            </div>

            {/* Mailing Address Section */}
            <div className="profile-section">
              <h3 className="profile-section-title">Mailing Address</h3>

              {/* Mailing Address - Editable */}
              <div
                className={`profile-field profile-field-full ${
                  isEditing ? "profile-field-editable" : ""
                }`}
              >
                <div className="profile-field-icon">
                  <FaMapMarkerAlt />
                </div>
                <div className="profile-field-content">
                  <label className="profile-field-label">Address</label>
                  {isEditing ? (
                    <textarea
                      name="mailingAddress"
                      className="form-textarea profile-textarea"
                      value={formData.mailingAddress}
                      onChange={handleInputChange}
                      placeholder="Enter your mailing address"
                      rows={3}
                    />
                  ) : (
                    <p className="profile-field-value profile-address">
                      {profile.mailingAddress || "Not provided"}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Additional Contact Section */}
            <div className="profile-section">
              <h3 className="profile-section-title">
                Additional Contact Information
              </h3>
              <div className="profile-field-items">
                {/* Secondary Email - Editable */}
                <div
                  className={`profile-field ${
                    isEditing ? "profile-field-editable" : ""
                  }`}
                >
                  <div className="profile-field-icon">
                    <FaEnvelope />
                  </div>
                  <div className="profile-field-content">
                    <label className="profile-field-label">
                      Secondary Email
                    </label>
                    {isEditing ? (
                      <>
                        <input
                          type="email"
                          name="secondaryEmail"
                          className="form-input profile-input"
                          value={formData.secondaryEmail}
                          onChange={handleInputChange}
                          placeholder="Enter secondary email"
                        />
                        <div className="profile-field-info">
                          <FaInfoCircle className="profile-info-icon" />
                          <span>
                            This email will also be able to access your project
                            information when used to log in.
                          </span>
                        </div>
                      </>
                    ) : (
                      <p className="profile-field-value">
                        {profile.secondaryEmail || "Not provided"}
                      </p>
                    )}
                  </div>
                </div>

                {/* Secondary Phone - Editable */}
                <div
                  className={`profile-field ${
                    isEditing ? "profile-field-editable" : ""
                  }`}
                >
                  <div className="profile-field-icon">
                    <FaPhone />
                  </div>
                  <div className="profile-field-content">
                    <label className="profile-field-label">
                      Secondary Phone
                    </label>
                    {isEditing ? (
                      <input
                        type="tel"
                        name="secondaryPhone"
                        className="form-input profile-input"
                        value={formData.secondaryPhone}
                        onChange={handleInputChange}
                        placeholder="Enter secondary phone"
                      />
                    ) : (
                      <p className="profile-field-value">
                        {formatPhoneDisplay(profile.secondaryPhone)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {/* Action Buttons */}
            <div className="profile-actions">
              {!isEditing ? (
                <button
                  className="btn btn-primary profile-edit-btn"
                  onClick={handleEditClick}
                >
                  <FaUserEdit />
                  <span>Update Information</span>
                </button>
              ) : (
                <div className="profile-edit-actions">
                  <button
                    className="btn btn-secondary profile-cancel-btn"
                    onClick={handleCancelClick}
                    disabled={isUpdating}
                  >
                    <FaTimes />
                    <span>Cancel</span>
                  </button>
                  <button
                    className="btn btn-primary profile-save-btn"
                    onClick={handleSaveClick}
                    disabled={isUpdating}
                  >
                    {isUpdating ? (
                      <>
                        <div className="spinner spinner-small"></div>
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <FaSave />
                        <span>Save</span>
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default ClientProfile;
